/** `/admin/tracks` — every circuit, its history and its editor.
 *
 * The editor works on one circuit and is reached from that circuit's page,
 * which is the right place for the button and the wrong place for it to be
 * the only one: an administrator looks for an admin tool in the admin panel.
 * This is where. Every circuit the service knows, the published ones first
 * and among them the ones with the most wrong with them, each one click from
 * its history — what arrived, what was decided, what was published — and,
 * where there is a published survey to correct, from its editor.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AdminHeader } from '@/components/admin/AdminHeader';
import { Card, ErrorNote, Kicker, Page } from '@/components/ui';
import { LoadingPanel } from '@/components/ui/Spinner';
import { listTracks } from '@/lib/api';
import { metres, percent, relativeTime } from '@/lib/format';
import type { Track } from '@/lib/types';

const MUTED = 'var(--color-neutral-500)';

/** The lower of the two sides: a circuit is as finished as its worse border. */
function worstSide(track: Track): number {
  const sides = [track.coverage.left, track.coverage.right].filter(
    (value): value is number => typeof value === 'number',
  );
  return sides.length > 0 ? Math.min(...sides) : 0;
}

/** What is most likely to want an editor, first: an open border, then the
 *  least surveyed. A closed lap at 100 % is last, and rightly. */
export function editOrder(a: Track, b: Track): number {
  if (a.coverage.closed !== b.coverage.closed) return a.coverage.closed ? 1 : -1;
  return worstSide(a) - worstSide(b) || a.name.localeCompare(b.name);
}

export default function AdminTracks() {
  const [tracks, setTracks] = useState<Track[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    listTracks()
      .then((reply) => {
        if (live) setTracks(reply.tracks);
      })
      .catch((thrown: unknown) => {
        if (live) setError(thrown);
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  const listed = useMemo(() => {
    const rows = tracks ?? [];
    const published = rows.filter((track) => track.last_published).sort(editOrder);
    const rest = rows
      .filter((track) => !track.last_published)
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...published, ...rest];
  }, [tracks]);

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <AdminHeader />

      <section aria-label="Track editor" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13.5, color: MUTED, maxWidth: 860 }}>
          Every circuit’s history is a click away: the surveys that arrived, what was decided about them, the pull
          requests and the issues. Open a published circuit’s editor to correct its map: keep records that should
          not be there off it and draw in a border nobody drove. Nothing is deleted, and nothing changes until the
          pull request the editor opens is merged. Published circuits come first, those with an open border before
          the rest.
        </p>

        {error ? (
          <ErrorNote error={error} title="The circuits could not be listed." onRetry={() => setAttempt((n) => n + 1)} />
        ) : tracks === null ? (
          <LoadingPanel label="listing the circuits" />
        ) : listed.length === 0 ? (
          <Card>
            <Kicker>no circuits yet</Kicker>
            <p style={{ margin: 0, fontSize: 13, color: MUTED }}>
              No survey has arrived. A circuit appears here with its first upload, and gains an editor once the
              merge job has published a survey of it.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {listed.map((track) => (
              <Card key={track.official_id} as="article" label={track.name} style={{ gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="truncate" style={{ fontSize: 14.5, fontWeight: 600 }} title={track.name}>
                      {track.name}
                    </div>
                    <div className="tabular-nums" style={{ fontSize: 12, color: MUTED }}>
                      {track.official_id} · {metres(track.length_m)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
                    <Link
                      to={`/admin/tracks/${encodeURIComponent(track.official_id)}`}
                      className="btn btn-secondary"
                      style={{ fontSize: 13, textDecoration: 'none' }}
                    >
                      History
                    </Link>
                    {track.last_published ? (
                      <Link
                        to={`/admin/tracks/${encodeURIComponent(track.official_id)}/edit`}
                        className="btn btn-primary"
                        style={{ fontSize: 13, textDecoration: 'none' }}
                      >
                        Edit
                      </Link>
                    ) : null}
                  </div>
                </div>
                {track.last_published ? (
                  <div className="tabular-nums" style={{ fontSize: 12.5, color: MUTED }}>
                    left {percent(track.coverage.left)} · right {percent(track.coverage.right)} ·{' '}
                    <span style={{ color: track.coverage.closed ? MUTED : 'var(--color-warn)' }}>
                      {track.coverage.closed ? 'closes into a lap' : 'a border is open'}
                    </span>
                  </div>
                ) : (
                  <div className="tabular-nums" style={{ fontSize: 12.5, color: MUTED }}>
                    {track.status} · {track.uploads} {track.uploads === 1 ? 'upload' : 'uploads'}
                    {track.held > 0 ? ` · ${track.held} held` : ''}
                    {track.pending > 0 ? ` · ${track.pending} pending` : ''}
                  </div>
                )}
                <div style={{ fontSize: 12, color: MUTED }}>
                  {track.last_published
                    ? `published ${relativeTime(track.last_published)}`
                    : track.last_upload_at
                      ? `last upload ${relativeTime(track.last_upload_at)}`
                      : 'nothing published'}{' '}
                  · <Link to={`/tracks/${encodeURIComponent(track.official_id)}`}>its page</Link>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </Page>
  );
}
